/**
 * useNotificationSettings
 *
 * Manages reading and writing a player's notification preferences from/to
 * the `player_notification_settings` table.
 *
 * Schema (confirmed from migration 20260926_player_notifications.sql):
 *   player_notification_settings:
 *     profile_id (uuid PK), new_photo_album (bool), game_score_posted (bool),
 *     claim_approved (bool), upcoming_game (bool), stat_adjustment_approved (bool),
 *     upcoming_game_hours_before (int), updated_at (timestamptz)
 *
 * Usage:
 *   const { settings, loading, saving, updateSetting, saveSettings } =
 *     useNotificationSettings(profileId);
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

export const NOTIFICATION_TYPE_META = [
  {
    key: "new_photo_album",
    label: "New Photo Albums",
    description: "Get notified when a new game photo album is posted.",
    icon: "camera",
    roles: ["player", "team_captain"],
  },
  {
    key: "game_score_posted",
    label: "Game Scores Posted",
    description: "Get notified when your team's game score is finalized.",
    icon: "trophy",
    roles: ["player", "team_captain"],
  },
  {
    key: "claim_approved",
    label: "Player Claim Approved",
    description:
      "Get notified when your player profile claim request is approved.",
    icon: "shield-check",
    roles: ["player", "team_captain"],
  },
  {
    key: "upcoming_game",
    label: "Upcoming Game Reminder",
    description: "Get a reminder before your team's next game.",
    icon: "calendar",
    roles: ["player", "team_captain"],
  },
  {
    key: "stat_adjustment_approved",
    label: "Stat Adjustment Approved",
    description: "Get notified when your stat adjustment request is approved.",
    icon: "clipboard-check",
    roles: ["team_captain"], // captain-only
  },
];

const DEFAULT_SETTINGS = {
  new_photo_album: true,
  game_score_posted: true,
  claim_approved: true,
  upcoming_game: true,
  stat_adjustment_approved: true,
  upcoming_game_hours_before: 24,
};

export function useNotificationSettings(profileId) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // ── Load settings from DB ───────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    setError(null);

    const { data, error: dbErr } = await supabase
      .from("player_notification_settings")
      .select("*")
      .eq("profile_id", profileId)
      .maybeSingle();

    if (dbErr) {
      console.error("[useNotificationSettings] load error:", dbErr);
      setError("Failed to load notification settings.");
    } else if (data) {
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    }
    // If no row, keep defaults — will be created on first save.
    setLoading(false);
  }, [profileId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // ── Update a single setting locally ────────────────────────────────────────
  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }, []);

  // ── Persist all settings to DB ──────────────────────────────────────────────
  const saveSettings = useCallback(async () => {
    if (!profileId) return { success: false };
    setSaving(true);
    setError(null);
    setSaved(false);

    const payload = {
      profile_id: profileId,
      new_photo_album: settings.new_photo_album,
      game_score_posted: settings.game_score_posted,
      claim_approved: settings.claim_approved,
      upcoming_game: settings.upcoming_game,
      stat_adjustment_approved: settings.stat_adjustment_approved,
      upcoming_game_hours_before: settings.upcoming_game_hours_before,
      updated_at: new Date().toISOString(),
    };

    const { error: dbErr } = await supabase
      .from("player_notification_settings")
      .upsert(payload, { onConflict: "profile_id" });

    if (dbErr) {
      console.error("[useNotificationSettings] save error:", dbErr);
      setError("Failed to save settings. Please try again.");
      setSaving(false);
      return { success: false, error: dbErr };
    }

    setSaved(true);
    setSaving(false);

    // Auto-clear the saved indicator after 3s
    setTimeout(() => setSaved(false), 3000);
    return { success: true };
  }, [profileId, settings]);

  return {
    settings,
    loading,
    saving,
    saved,
    error,
    updateSetting,
    saveSettings,
    reload: loadSettings,
  };
}
